import { Fragment, useEffect, useState, type ReactNode } from "react";
import type { View } from "../../../../common/types.ts";
import { LeagueDomainNav } from "../components/LeagueDomainNav.tsx";
import MobileListToggle, { MOBILE_LIST_INITIAL_ITEMS } from "../components/MobileListToggle.tsx";
import TeamRankingTable, {
  type TeamRankingColumn,
  type TeamRankingRow,
  type TeamRankingSection,
} from "../components/TeamRankingTable.tsx";
import {
  formatTeamRankingGamesBack,
  formatTeamRankingPercentage,
  getTeamRankingSeasonStarted,
  getTeamRankingZone,
  type TeamRankingZone,
} from "../components/teamRankingTableState.ts";
import { TeamRosterSheet } from "../components/TeamRosterSheet.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { MobileStatInsights } from "../mobile/MobileStatInsights.tsx";
import { MobileContentHero, MobileStatRow } from "../mobile/mobilePrimitives.tsx";
import {
  getStandingsTeamInsights,
  type StandingsInsightKind,
  type StandingsTeamInsight,
} from "../mobile/statInsights.ts";
import { translateConfName } from "../util/confDivNames.ts";
import i18n from "../util/i18n.ts";
import { translateTeamName, translateTeamRegion } from "../util/teamNames.ts";
import {
  getCollapsedStandingsTeams,
  standingsQualificationAppliesToScope,
} from "./standingsCompactState.ts";

type StandingsTeam = View<"standings">["rankingGroups"]["league"][number][number];
type StandingsType = View<"standings">["type"];

type StandingsSubgroup = {
  name?: string;
  showQualificationZones: boolean;
  teams: StandingsTeam[];
};

type StandingsGroup = {
  name?: string;
  subgroups: StandingsSubgroup[];
};

const translateStreak = (streak: string): string | undefined => {
  if (streak === "None") {
    return undefined;
  }

  const match = /^(Won|Lost) (\d+)$/.exec(streak);
  if (match) {
    const count = Number(match[2]);
    const won = match[1] === "Won";

    return i18n.t(won ? "standings.streakWon" : "standings.streakLost", {
      ns: "ui",
      count,
      defaultValue: won ? "Won {{count}}" : "Lost {{count}}",
    });
  }

  return streak || undefined;
};

const getStandingsTypeLabel = (standingsType: StandingsType) => {
  if (standingsType === "conf") {
    return i18n.t("standings.viewConference", {
      ns: "ui",
      defaultValue: "Conference",
    });
  }

  return i18n.t("standings.viewLeague", {
    ns: "ui",
    defaultValue: "Overall",
  });
};

const formatStandingsInsightValue = (kind: StandingsInsightKind, value: number) =>
  kind === "net-margin" ? `${value >= 0 ? "+" : ""}${value.toFixed(1)}` : value.toFixed(1);

const getStandingsInsightConclusion = (insight: StandingsTeamInsight<StandingsTeam>) => {
  const value = formatStandingsInsightValue(insight.kind, insight.conclusion.value);
  const metricText =
    insight.kind === "net-margin"
      ? i18n.t("statInsights.netMargin", {
          ns: "ui",
          defaultValue: "{{value}} average point margin",
          value,
        })
      : insight.kind === "defense"
        ? i18n.t("statInsights.defense", {
            ns: "ui",
            defaultValue: "{{value}} points allowed per game",
            value,
          })
        : i18n.t("statInsights.offense", {
            ns: "ui",
            defaultValue: "{{value}} points scored per game",
            value,
          });
  const rankText = insight.conclusion.placement.tied
    ? i18n.t("statInsights.leagueRankTied", {
        ns: "ui",
        defaultValue: "tied for league #{{rank}}",
        rank: insight.conclusion.placement.rank,
      })
    : i18n.t("statInsights.leagueRank", {
        ns: "ui",
        defaultValue: "league #{{rank}}",
        rank: insight.conclusion.placement.rank,
      });

  return `${metricText} · ${rankText}`;
};

const getStandingsInsightTeamLabel = (team: StandingsTeam) => {
  const region = translateTeamRegion(team.seasonAttrs.region);
  const name = team.seasonAttrs.name ? ` ${translateTeamName(team.seasonAttrs.name)}` : "";
  return `${region}${name}`;
};

const getZoneLabel = (zone: TeamRankingZone): ReactNode => {
  if (zone === "playoffs") {
    return i18n.t("standings.playoffZone", {
      ns: "ui",
      defaultValue: "Playoff zone",
    });
  }
  if (zone === "playIn") {
    return i18n.t("standings.playInZone", {
      ns: "ui",
      defaultValue: "Play-in zone",
    });
  }
  return i18n.t("standings.outsideZone", {
    ns: "ui",
    defaultValue: "Outside postseason",
  });
};

const getColumns = (usePts: boolean): TeamRankingColumn[] => [
  {
    key: "wins",
    kind: "number",
    label: i18n.t("standings.wins", { ns: "ui", defaultValue: "W" }),
  },
  {
    key: "losses",
    kind: "number",
    label: i18n.t("standings.losses", { ns: "ui", defaultValue: "L" }),
  },
  {
    key: "winPercentage",
    kind: "percentage",
    label: i18n.t(usePts ? "standings.pointsPercentage" : "standings.winningPercentage", {
      ns: "ui",
      defaultValue: usePts ? "PTS%" : "Win%",
    }),
  },
  {
    key: "secondary",
    kind: "number",
    label: i18n.t(usePts ? "standings.pointsShort" : "standings.gamesBackShort", {
      ns: "ui",
      defaultValue: usePts ? "PTS" : "GB",
    }),
  },
  {
    key: "recent",
    kind: "text",
    label: i18n.t("standings.recent", { ns: "ui", defaultValue: "Recent" }),
  },
];

const getRankingRow = ({
  t,
  type,
  usePts,
  userTid,
}: {
  t: StandingsTeam;
  type: StandingsType;
  usePts: boolean;
  userTid: number;
}): TeamRankingRow => {
  const recent = translateStreak(t.seasonAttrs.streak);
  return {
    imgURL: t.seasonAttrs.imgURL,
    imgURLSmall: t.seasonAttrs.imgURLSmall,
    isUserTeam: t.tid === userTid,
    rank: t.rank[type] > 0 ? t.rank[type] : null,
    teamName: (
      <span
        data-standings-row=""
        data-standings-team-id={t.tid}
        data-standings-user-team={t.tid === userTid ? "true" : undefined}
        data-standings-user-team-highlight={t.tid === userTid ? "true" : undefined}
      >
        {translateTeamRegion(t.seasonAttrs.region)}
        {t.seasonAttrs.name ? ` ${translateTeamName(t.seasonAttrs.name)}` : ""}
      </span>
    ),
    tid: t.tid,
    values: {
      losses: t.seasonAttrs.lost,
      recent,
      secondary: usePts ? Math.round(t.seasonAttrs.pts) : formatTeamRankingGamesBack(t.gb[type]),
      winPercentage: formatTeamRankingPercentage(
        usePts ? t.seasonAttrs.ptsPct : t.seasonAttrs.winp,
      ),
      wins: t.seasonAttrs.won,
    },
  };
};

const getRankingSections = ({
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  playIn,
  showQualificationZones,
  teams,
  type,
  usePts,
  userTid,
}: {
  maxPlayoffSeed: number;
  maxPlayoffSeedNoPlayIn: number;
  playIn: boolean;
  showQualificationZones: boolean;
  teams: StandingsTeam[];
  type: StandingsType;
  usePts: boolean;
  userTid: number;
}): TeamRankingSection[] => {
  const sections: TeamRankingSection[] = [];

  for (const t of teams) {
    const zone = getTeamRankingZone({
      maxPlayoffSeed,
      maxPlayoffSeedNoPlayIn,
      playIn,
      qualificationApplies: showQualificationZones,
      rank: t.rank[type],
    });
    const id = zone;
    let section = sections.at(-1);
    if (!section || section.id !== id) {
      section = {
        id,
        label: zone !== "none" ? getZoneLabel(zone) : undefined,
        rows: [],
        zone,
      };
      sections.push(section);
    }
    (section.rows as TeamRankingRow[]).push(getRankingRow({ t, type, usePts, userTid }));
  }

  return sections;
};

const GroupStandings = ({
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  name,
  onOpenRoster,
  playIn,
  season,
  seasonStarted,
  showQualificationZones,
  teams,
  type,
  usePts,
  userTid,
}: Pick<
  View<"standings">,
  "maxPlayoffSeed" | "maxPlayoffSeedNoPlayIn" | "playIn" | "season" | "type" | "usePts" | "userTid"
> & {
  name?: string;
  onOpenRoster: (tid: number) => void;
  seasonStarted: boolean;
  showQualificationZones: boolean;
  teams: StandingsTeam[];
}) => {
  const [expanded, setExpanded] = useState(false);
  const teamKeySignature = teams.map((t) => String(t.tid)).join("\u0001");

  useEffect(() => {
    setExpanded(false);
  }, [teamKeySignature]);

  const canCollapse = teams.length > MOBILE_LIST_INITIAL_ITEMS;
  const separatorIndexes = showQualificationZones
    ? [maxPlayoffSeedNoPlayIn, maxPlayoffSeed].filter(
        (index, position, indexes) =>
          index >= 0 && index < teams.length && indexes.indexOf(index) === position,
      )
    : [];
  const teamsToRender = getCollapsedStandingsTeams({
    canCollapse,
    expanded,
    separatorIndexes,
    teams,
    userTid,
    visibleCount: MOBILE_LIST_INITIAL_ITEMS,
  });
  const sections = getRankingSections({
    maxPlayoffSeed,
    maxPlayoffSeedNoPlayIn,
    playIn,
    showQualificationZones,
    teams: teamsToRender,
    type,
    usePts,
    userTid,
  });

  return (
    <section
      data-standings-subgroup=""
      data-standings-view-type={type}
      data-standings-window-state={expanded ? "full" : "nearby"}
    >
      {name ? <h3 className="h5 mb-2">{name}</h3> : null}
      <div
        data-standings-compact-list=""
        data-standings-nearby-window={expanded ? "false" : "true"}
        data-standings-rendered-rows={seasonStarted ? teamsToRender.length : 0}
        data-standings-total-rows={teams.length}
      >
        <TeamRankingTable
          ariaLabel={
            name ??
            i18n.t("standings.seasonTitle", {
              ns: "ui",
              season,
              defaultValue: "{{season}} standings",
            })
          }
          columns={getColumns(usePts)}
          onTeamSelect={onOpenRoster}
          rankLabel={i18n.t("standings.rankLabel", {
            ns: "ui",
            defaultValue: "Rank",
          })}
          seasonNotStartedLabel={i18n.t("standings.seasonNotStarted", {
            ns: "ui",
            defaultValue: "The season has not started",
          })}
          seasonStarted={seasonStarted}
          sections={sections}
          teamLabel={i18n.t("standings.team", {
            ns: "ui",
            defaultValue: "Team",
          })}
        />
      </div>
      {canCollapse && seasonStarted ? (
        <div data-standings-full-table-depth="">
          <MobileListToggle
            expanded={expanded}
            onClick={() => {
              setExpanded((previous) => !previous);
            }}
          />
        </div>
      ) : null}
    </section>
  );
};

const Standings = ({
  confs,
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  playIn,
  playoffsByConf,
  rankingGroups,
  season,
  type,
  usePts,
  userTid,
}: View<"standings">) => {
  useTitleBar({
    title: i18n.t("standings.seasonTitle", {
      ns: "ui",
      season,
      defaultValue: "{{season}} standings",
    }),
    jumpTo: true,
    jumpToSeason: season,
    dropdownView: "standings",
    dropdownFields: {
      seasons: season,
      standingsType: type,
    },
  });

  const [rosterSheetTid, setRosterSheetTid] = useState<number | undefined>();
  let groups: StandingsGroup[];
  if (type === "league") {
    groups = [
      {
        subgroups: [
          {
            showQualificationZones: standingsQualificationAppliesToScope({
              confCount: confs.length,
              playoffsByConf: Boolean(playoffsByConf),
              type,
            }),
            teams: rankingGroups.league[0]!,
          },
        ],
      },
    ];
  } else {
    groups = confs.map((conf, index) => ({
      name: translateConfName(conf.name),
      subgroups: [
        {
          showQualificationZones: standingsQualificationAppliesToScope({
            confCount: confs.length,
            playoffsByConf: Boolean(playoffsByConf),
            type,
          }),
          teams: rankingGroups.conf[index]!,
        },
      ],
    }));
  }

  const allTeams = rankingGroups.league[0] ?? [];
  const seasonStarted = getTeamRankingSeasonStarted(allTeams.map((team) => team.seasonAttrs));
  const standingsInsights = getStandingsTeamInsights(allTeams, userTid);
  const standingsInsightItems = standingsInsights.map((insight) => ({
    conclusion: getStandingsInsightConclusion(insight),
    evidence: insight.conclusion.evidence.map((row) => (
      <MobileStatRow
        highlighted={row.isSubject}
        key={row.subject.tid}
        label={getStandingsInsightTeamLabel(row.subject)}
        rank={`#${row.rank}`}
        value={formatStandingsInsightValue(insight.kind, row.value)}
      />
    )),
    id: `standings-${insight.kind}`,
    tone: "positive" as const,
  }));
  const standingsContextSummary = i18n.t("standings.contextSummary", {
    ns: "ui",
    season,
    view: getStandingsTypeLabel(type),
    defaultValue: "{{season}} regular season · {{view}}",
  });

  return (
    <>
      <MobileContentHero
        data-desktop-production-view="standings"
        data-standings-content-first=""
        data-standings-context={type}
        data-v5-standings-table-first-screen=""
        explainer={standingsContextSummary}
      >
        <LeagueDomainNav />
        {standingsInsightItems.length > 0 ? (
          <MobileStatInsights
            className="mb-3"
            data-standings-stat-insights=""
            items={standingsInsightItems}
          />
        ) : null}
        {groups.map(({ name, subgroups }, groupIndex) => (
          <Fragment key={groupIndex}>
            {name ? <h2>{name}</h2> : null}
            {subgroups.map((subgroup, subgroupIndex) => (
              <GroupStandings
                key={subgroupIndex}
                {...subgroup}
                maxPlayoffSeed={maxPlayoffSeed}
                maxPlayoffSeedNoPlayIn={maxPlayoffSeedNoPlayIn}
                onOpenRoster={setRosterSheetTid}
                playIn={playIn}
                season={season}
                seasonStarted={seasonStarted}
                type={type}
                usePts={usePts}
                userTid={userTid}
              />
            ))}
          </Fragment>
        ))}
      </MobileContentHero>
      <TeamRosterSheet
        onClose={() => setRosterSheetTid(undefined)}
        open={rosterSheetTid !== undefined}
        season={season}
        tid={rosterSheetTid}
      />
    </>
  );
};

export default Standings;
